import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { MdOutlineDelete } from 'react-icons/md';
import { Button, Input } from '@ds';
import { MainLocationsInputs } from '@lp/features/RoutePlanner/components/RouteLegForm/components/locations';
import { BaseCommercialAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useWatchFields } from '@/hooks/useWatchFields';
import { ConnectedLegsForm } from './ConnectedLegsForm';

export const CommercialFlightInputs = () => {
  const { isMainDataEdit, isOptionalDataEdit } = useLegFormContext();
  const {
    control,
    setValue,
    reset,
    formState: { defaultValues },
  } = useFormContext<BaseCommercialAviaLegSchemaValues>();
  const { connectedLegs, commercialFlight, resources, comments } = useWatchFields<BaseCommercialAviaLegSchemaValues>([
    'connectedLegs',
    'commercialFlight',
    'resources',
    'comments',
  ] as const);

  const removeConnectedLegs = () => {
    if (connectedLegs.length) {
      reset(defaultValues);
      setValue('resources', resources);
      setValue('comments', comments);
    }
  };

  return (
    <>
      {!connectedLegs.length ? (
        <>
          <MainLocationsInputs />
          <div className="flex gap-4">
            <div className="w-60">
              <Controller
                name="flightNumber"
                control={control}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    size="md"
                    aria-label="Flight Number *"
                    label="Flight Number *"
                    placeholder="Flight Number"
                    isInvalid={invalid}
                    isDisabled={!isMainDataEdit}
                    {...field}
                  />
                )}
              />
            </div>
            <div className="w-[140px]">
              <Controller
                name="bookingNumber"
                control={control}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    size="md"
                    aria-label="Booking Number"
                    label="Booking Number"
                    placeholder="Booking Number"
                    isInvalid={invalid}
                    isDisabled={!isOptionalDataEdit}
                    {...field}
                  />
                )}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center">
            <span className="text-l-sb text-base-5">Connected Legs</span>
            <Button
              variant="light"
              size="sm"
              startContent={<MdOutlineDelete size={18} />}
              onPress={removeConnectedLegs}
            >
              Delete legs
            </Button>
          </div>
          {connectedLegs.map((l, index) => (
            <ConnectedLegsForm key={l.flightNumber} connectedLeg={commercialFlight?.legs[index]} index={index} />
          ))}
        </div>
      )}
      <div className="w-[130px]">
        <Controller
          name="priceInCents"
          control={control}
          render={({ field: { onChange, ref, ...field }, fieldState: { invalid } }) => (
            <NumericFormat
              prefix="$"
              thousandSeparator
              valueIsNumericString
              decimalScale={2}
              customInput={Input}
              aria-label="Price"
              label="Price"
              placeholder="-"
              classNames={{
                inputWrapper: 'bg-base-60! rounded-lg',
              }}
              isInvalid={invalid}
              isDisabled={!isOptionalDataEdit}
              onValueChange={(values) => {
                onChange(values.value);
              }}
              {...field}
            />
          )}
        />
      </div>
    </>
  );
};
